import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import { sendSignedDocumentNotification } from '../utils/emailService';
import { fetchPublicDocumentForViewing } from '../utils/publicDocumentService';
import './CompletePage.css';

// Set up the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const CompletePage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const documentContainerRef = useRef(null);
  
  const [document, setDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [loadingDocument, setLoadingDocument] = useState(true);
  const [pages, setPages] = useState([]);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  // Load document data
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoadingDocument(true);
        setError(null);
        
        const doc = await fetchPublicDocumentForViewing(documentId);
        console.log('Loaded document:', doc);
        console.log('Signed data:', doc.signed_data || doc.signedData);
        setDocument(doc);
        
        // Create a download URL from the file data
        if (doc.file_data && doc.file_data.startsWith('STORAGE_URL:')) {
          // Use the storage URL for download
          setDownloadUrl(doc.file_data.substring('STORAGE_URL:'.length));
        } else if (doc.fileData && doc.fileData.startsWith('data:')) {
          // Use the base64 data URL for download
          setDownloadUrl(doc.fileData);
        }
        
        setLoadingDocument(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Failed to load document. It may not exist, has been deleted, or is not yet signed.');
        setLoadingDocument(false);
      }
    };
    
    loadDocument();
  }, [documentId]);

  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPages(Array.from(new Array(numPages), (_, index) => index + 1));
  };

  // Handle sending email notification
  const handleSendEmailNotification = async () => {
    if (!document) return;
    
    try {
      setSendingEmail(true);
      setEmailError(null);
      
      // Generate the view link for the signed document
      const viewLink = `${window.location.origin}/complete/${documentId}`;
      
      const signedData = document.signed_data || document.signedData || {};
      const signerEmail = signedData.signerEmail || 'Unknown';
      const recipientEmail = document.recipientEmail || document.recipient_email;
      
      // Determine if this is the final signature based on document status
      const isFinalSignature = document.status === 'signed';
      
      console.log('Manually sending notification to document owner:', recipientEmail);
      console.log('Manually sending notification to signer:', signerEmail);
      console.log('Is final signature:', isFinalSignature);
      
      const notificationResult = await sendSignedDocumentNotification({
        documentId,
        recipientEmail,
        signerEmail,
        viewLink,
        isFinalSignature
      });
      
      console.log('Manual notification result:', notificationResult);
      
      setEmailSent(true);
      setSendingEmail(false);
    } catch (error) {
      console.error('Error sending email notification:', error);
      setEmailError('Failed to send email notification. Please try again.');
      setSendingEmail(false);
    }
  };

  // Handle download with signatures embedded
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  
  const handleDownload = async () => {
    try {
      setIsGeneratingPdf(true);
      
      // Get all page elements
      const pageElements = window.document.querySelectorAll('.document-page-wrapper');
      if (!pageElements || pageElements.length === 0) {
        throw new Error('No document pages found');
      }
      
      // Create a new PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: 'a4',
      });
      
      // Process each page
      for (let i = 0; i < pageElements.length; i++) {
        // If not the first page, add a new page to the PDF
        if (i > 0) {
          pdf.addPage();
        }
        
        // Capture the page with signatures as an image
        const canvas = await html2canvas(pageElements[i], {
          scale: 2, // Higher scale for better quality
          useCORS: true, // Allow loading cross-origin images
          allowTaint: true,
          backgroundColor: '#ffffff',
        });
        
        // Add the image to the PDF
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }
      
      // Save the PDF
      pdf.save(`${document.name} (Signed).pdf`);
      setIsGeneratingPdf(false);
      
    } catch (error) {
      console.error('Error generating signed PDF:', error);
      alert('Error generating the signed document. Please try again.');
      setIsGeneratingPdf(false);
      
      // Fallback to original download method if PDF generation fails
      if (downloadUrl) {
        try {
          const a = window.document.createElement('a');
          a.href = downloadUrl;
          a.download = `${document.name} (Original).pdf`;
          a.target = '_blank';
          window.document.body.appendChild(a);
          a.click();
          window.document.body.removeChild(a);
          console.log('Fallback download initiated with URL');
        } catch (downloadError) {
          console.error('Error during fallback download:', downloadError);
        }
      }
    }
  };

  // If document is loading, show loading
  if (loadingDocument) {
    return (
      <div className="complete-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading document...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="complete-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Error" 
            message={error} 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If document is not yet loaded, show loading
  if (!document) {
    return (
      <div className="complete-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Document Not Found" 
            message="The document you're looking for doesn't exist, has been deleted, or is not yet signed." 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (document.status !== 'signed' && document.status !== 'partially_signed') {
    return (
      <div className="complete-page">
        <div className="container">
          <Card className="info-card">
            <h2 className="info-title">Document Not Yet Signed</h2>
            <p className="info-message">
              This document is waiting to be signed. Once signed, you'll be able to view the completed document here.
            </p>
            <div className="text-center mt-lg">
              <Button onClick={() => navigate('/')}>
                Return to Home
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  const signedData = document.signed_data || document.signedData || {};
  const signerName = signedData.signerName || 'Unknown';
  const signerEmail = signedData.signerEmail || 'Unknown';
  const signedAt = signedData.signedAt ? new Date(signedData.signedAt).toLocaleString() : 'Unknown';

  return (
    <div className="complete-page">
      <div className="container">
        <div className="complete-header">
          <h1 className="page-title">Completed Document: {document.name}</h1>
          <div className="complete-actions">
            <Button 
              variant="secondary" 
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
            <Button onClick={handleDownload}>
              Download Signed Document
            </Button>
          </div>
        </div>
        
        <div className="complete-content">
          <div className="document-preview" ref={documentContainerRef}>
            <Document
              file={document.file_data && document.file_data.startsWith('STORAGE_URL:') 
                ? document.file_data.substring('STORAGE_URL:'.length) 
                : document.fileData}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="loading-spinner"></div>}
              error={<Alert type="error" message="Failed to load PDF document" />}
            >
              <div className="document-pages-container">
                {pages.map(pageNumber => (
                  <div key={pageNumber} className="document-page-wrapper">
                    <div className="document-page-overlay">
                      {document.fields
                        .filter(field => field.pageNumber === pageNumber)
                        .map(field => {
                          const signature = signedData.signatures?.[field.id];
                          if (!signature) return null;
                          
                          return (
                            <div
                              key={field.id}
                              className={`signature-field signature-field-${field.type} signed`}
                              style={{
                                left: `${field.position.x}px`,
                                top: `${field.position.y}px`,
                                width: field.type === 'signature' ? '150px' : field.type === 'initial' ? '60px' : '120px',
                                height: field.type === 'signature' ? '60px' : field.type === 'initial' ? '40px' : '40px',
                              }}
                            >
                              {field.type === 'date' ? (
                                <div className="date-value">{signature}</div>
                              ) : (
                                <img 
                                  src={signature} 
                                  alt={`${field.type} field`} 
                                  className="signature-image"
                                />
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <Page 
                      pageNumber={pageNumber} 
                      width={800}
                      className="document-page"
                    />
                  </div>
                ))}
              </div>
            </Document>
          </div>
          
          <div className="complete-sidebar">
            <Card className="signature-info-card">
              <h3 className="signature-info-title">Signature Information</h3>
              
              <div className="signature-info-content">
                <div className="info-group">
                  <div className="info-label">Document Name</div>
                  <div className="info-value">{document.name}</div>
                </div>
                
                <div className="info-group">
                  <div className="info-label">Status</div>
                  <div className="info-value">
                    <span className={`document-status ${document.status === 'partially_signed' ? 'status-partial' : 'status-signed'}`}>
                      {document.status === 'partially_signed' ? 'Partially Signed' : 'Signed'}
                    </span>
                  </div>
                </div>
                
                <div className="info-group">
                  <div className="info-label">Signed By</div>
                  <div className="info-value">{signerName}</div>
                </div>
                
                <div className="info-group">
                  <div className="info-label">Signer Email</div>
                  <div className="info-value">{signerEmail}</div>
                </div>
                
                <div className="info-group">
                  <div className="info-label">Signed On</div>
                  <div className="info-value">{signedAt}</div>
                </div>
                
                {document.status === 'partially_signed' && document.recipients && document.recipients.length > 0 && (
                  <div className="info-group">
                    <div className="info-label">Waiting For</div>
                    <div className="info-value">
                      {document.recipients
                        .filter(recipient => {
                          // Check if this recipient has not signed yet
                          return !(document.signatures || []).some(sig => 
                            sig.signerEmail === recipient.email
                          );
                        })
                        .map((recipient, index) => (
                          <div key={index} className="waiting-recipient">
                            {recipient.email}
                          </div>
                        ))
                      }
                    </div>
                  </div>
                )}
                
                <div className="info-group">
                  <div className="info-label">Recipient</div>
                  <div className="info-value">{document.recipientEmail || document.recipient_email || 'Not specified'}</div>
                </div>
              </div>
              
              <div className="signature-info-actions">
              <Button 
                onClick={handleDownload}
                loading={isGeneratingPdf}
                disabled={isGeneratingPdf}
                fullWidth
              >
                {isGeneratingPdf ? 'Generating PDF...' : 'Download Signed Document'}
              </Button>
                
                {(document.recipientEmail || document.recipient_email) && (
                  <div className="mt-md">
                    {emailSent ? (
                      <Alert 
                        type="success" 
                        message="Email notification sent successfully!" 
                      />
                    ) : emailError ? (
                      <Alert 
                        type="error" 
                        message={emailError} 
                        dismissible={true}
                        onDismiss={() => setEmailError(null)}
                      />
                    ) : (
                      <Button 
                        variant="secondary"
                        onClick={handleSendEmailNotification}
                        loading={sendingEmail}
                        disabled={sendingEmail}
                        fullWidth
                      >
                        Resend Email Notification
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletePage;
